<script setup lang="ts">
import { shallowRef, watch, onMounted, onUnmounted } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import MainLayout from './pages/layouts/MainLayout.vue'
import EmptyLayout from './pages/layouts/EmptyLayout.vue'
import TheLoader from './components/atoms/TheLoader/TheLoader.vue'
import MultiTabWarning from './components/organisms/MultiTabWarning/MultiTabWarning.vue'
import VersionUpdateBanner from './components/organisms/VersionUpdateBanner/VersionUpdateBanner.vue'
import ErrorDetailsModal from './components/organisms/ErrorDetailsModal/ErrorDetailsModal.vue'
import SupportWidget from './components/organisms/SupportWidget/SupportWidget.vue'
import { useUserStore } from '@/stores/userStore'
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useHostNameStore } from '@/stores/useHostNameStore'
import { useTabSync } from '@/composables/useTabSync'
import { useDeploymentDetection } from '@/composables/useDeploymentDetection'
import { useSessionTracker } from '@/composables/useSessionTracker'
import { endSession } from '@/api/sessionApi'

const route = useRoute()
const layout = shallowRef()

// Initialize tab sync for cross-tab communication
const tabSync = useTabSync()

// Initialize session tracking
const { startTracking, stopTracking, tabId } = useSessionTracker()

// Detect new deployments and prompt user to refresh
const { deploymentDetected } = useDeploymentDetection()

const authStore = useAuthenticationStore()

// Track route changes and notify other tabs
watch(() => route.name, (newRouteName) => {
    if (newRouteName) {
        tabSync.setCurrentRoute(newRouteName as string)
        
        // Track sessions for all users (visitors and authenticated)
        // Auto-save will only trigger on createEventStep2/Step3 (filtered in useSessionTracker)
        startTracking(newRouteName as string)
    }
}, { immediate: true })

// Manage session lifecycle based on auth state
watch(() => authStore.userId, (newUserId, oldUserId) => {
    // Send immediate heartbeat on identity change to link/update session
    if (route.name) {
        startTracking(route.name as string)
    }

    if (!newUserId && oldUserId) {
        // User logged out
        endSession(oldUserId, tabId)
    }
})

watch(route, (to) => {
    if (to.meta?.layout === 'EmptyLayout') {
        layout.value = EmptyLayout
    } else {
        layout.value = MainLayout
    }
})
useUserStore()

onMounted(() => {
    const appElement = document.getElementById("app");
    // Use the hostname store (it may return an emulated host from localStorage)
    const hostNameStore = useHostNameStore()
    let hostname = hostNameStore.hostname || window.location.hostname

    // Add short class (first label) and dashed class (domain with dashes) for selectors
    const short = hostname.split(".")[0]
    if (short && appElement) {
        appElement.classList.add(short);
        document.body.classList.add(short);
    }

    const dashed = hostname.replace(/^v2\./, '').replace(/^staging\./, '').replace(/\./g, '-')
    if (dashed && appElement) {
        appElement.classList.add(dashed);
        document.body.classList.add(dashed);
    }
})

onUnmounted(() => {
    stopTracking()
})
</script>

<template>
    <VersionUpdateBanner />
    <MultiTabWarning />
    <TheLoader />
    <ErrorDetailsModal />
    <SupportWidget v-if="authStore.isLoggedIn" />
    <component :is="layout">
        <RouterView v-slot="{ Component }">
            <Transition name="fade">
                <component :is="Component" />
            </Transition>
        </RouterView>
    </component>
</template>
<style lang="scss" scoped>
.fade-enter-active {
    transition: all 0.3s ease-out;
}

.fade-leave-active {
    transition: all 0.3s ease-in;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
